import React, { useState, useRef } from "react";
import axios from "axios";
import { X, Plus, Trash2, ChevronDown, Images } from "lucide-react";
import { useAdmin } from "../contexts/AdminContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_PHOTOS = 9;

/**
 * Photo gallery dropdown for P5 & P6 — opens a 3×3 grid of up to 9 photos.
 * Click any photo to view it large.
 */
export default function PhotoGallery({ section, isNight, onUpdated }) {
    const accent = isNight ? "#D4AF37" : "#B89B5E";
    const { isAdmin } = useAdmin();
    const photos = section?.media?.photos || [];

    const [open, setOpen] = useState(false);
    const [lightbox, setLightbox] = useState(null); // photo URL
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const onPickFiles = async (files) => {
        if (!files || files.length === 0) return;
        const remaining = MAX_PHOTOS - photos.length;
        if (remaining <= 0) return;
        setUploading(true);
        try {
            let updated = section;
            for (const f of Array.from(files).slice(0, remaining)) {
                const fd = new FormData();
                fd.append("file", f);
                fd.append("kind", "photo");
                const res = await axios.post(`${API}/sections/${section.id}/media`, fd);
                updated = res.data;
            }
            onUpdated && onUpdated(updated);
        } catch (e) {
            console.error("Photo upload failed", e);
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const onDelete = async (i) => {
        try {
            const res = await axios.delete(`${API}/sections/${section.id}/media`, {
                params: { kind: "photo", index: i },
            });
            onUpdated && onUpdated(res.data);
        } catch (e) { console.error(e); }
    };

    return (
        <>
            <div
                data-testid={`photo-gallery-trigger-${section.id}`}
                className="absolute z-30 top-28 right-6 md:right-12 pointer-events-auto"
            >
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    data-testid={`photo-gallery-toggle-${section.id}`}
                    className={`${isNight ? "glass" : "glass-day"} flex items-center gap-2 px-4 py-2 rounded-full transition-all hover:scale-[1.02]`}
                    style={{ border: `1px solid ${accent}55`, color: isNight ? "#fff" : "#0a0a0a" }}
                >
                    <Images size={14} style={{ color: accent }} />
                    <span className="text-[10px] tracking-[0.3em] uppercase">
                        Photo Gallery · {photos.length}/{MAX_PHOTOS}
                    </span>
                    <ChevronDown
                        size={14}
                        style={{
                            color: accent,
                            transform: open ? "rotate(180deg)" : "none",
                            transition: "transform 0.2s ease",
                        }}
                    />
                </button>

                {/* Dropdown panel */}
                {open && (
                    <div
                        data-testid={`photo-gallery-panel-${section.id}`}
                        className={`mt-2 rounded-2xl p-4 w-[min(90vw,460px)] ${isNight ? "glass" : "glass-day"}`}
                        style={{ border: `1px solid ${accent}55` }}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div
                                className="text-[10px] tracking-[0.3em] uppercase"
                                style={{ color: accent }}
                            >
                                {section.label} · Related Spaces
                            </div>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                data-testid={`photo-gallery-close-${section.id}`}
                                style={{ color: isNight ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)" }}
                            >
                                <X size={14} />
                            </button>
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                            {Array.from({ length: MAX_PHOTOS }).map((_, i) => {
                                const url = photos[i];
                                return (
                                    <div
                                        key={i}
                                        data-testid={`photo-slot-${section.id}-${i}`}
                                        className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
                                        style={{
                                            background: isNight ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
                                            border: `1px ${url ? "solid" : "dashed"} ${accent}55`,
                                        }}
                                        onClick={() => url ? setLightbox(url) : inputRef.current?.click()}
                                    >
                                        {url ? (
                                            <>
                                                <img
                                                    src={url}
                                                    alt={`Photo ${i + 1}`}
                                                    className="absolute inset-0 w-full h-full object-cover"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); onDelete(i); }}
                                                    data-testid={`photo-delete-${section.id}-${i}`}
                                                    className="absolute top-1 right-1 w-6 h-6 rounded-full grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                    style={{
                                                        background: "rgba(0,0,0,0.7)",
                                                        color: "#fff",
                                                        visibility: isAdmin ? "visible" : "hidden",
                                                    }}
                                                >
                                                    <Trash2 size={11} />
                                                </button>
                                            </>
                                        ) : (
                                            <div
                                                className="absolute inset-0 grid place-items-center"
                                                style={{ color: accent }}
                                            >
                                                <Plus size={18} />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp"
                            multiple
                            className="hidden"
                            data-testid={`photo-input-${section.id}`}
                            onChange={(e) => onPickFiles(e.target.files)}
                        />

                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={photos.length >= MAX_PHOTOS || uploading || !isAdmin}
                            data-testid={`photo-upload-${section.id}`}
                            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-[10px] tracking-[0.3em] uppercase transition-all disabled:opacity-40"
                            style={{ background: accent, color: "#0a0a0a" }}
                        >
                            <Plus size={12} />
                            {!isAdmin ? "Admin only" : (uploading ? "Uploading…" : photos.length >= MAX_PHOTOS ? "Gallery Full" : "Add Photos")}
                        </button>
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightbox && (
                <div
                    data-testid={`photo-lightbox-${section.id}`}
                    className="absolute inset-0 z-50 grid place-items-center px-4 py-12"
                    style={{ background: "rgba(0,0,0,0.92)", backdropFilter: "blur(10px)" }}
                    onClick={() => setLightbox(null)}
                >
                    <button
                        type="button"
                        onClick={() => setLightbox(null)}
                        data-testid={`photo-lightbox-close-${section.id}`}
                        className="absolute top-6 right-6 w-10 h-10 rounded-full grid place-items-center"
                        style={{ background: `${accent}20`, color: accent, border: `1px solid ${accent}55` }}
                    >
                        <X size={18} />
                    </button>
                    <img
                        src={lightbox}
                        alt="Photo"
                        className="max-w-[92vw] max-h-[80vh] rounded-2xl"
                        style={{ boxShadow: `0 30px 80px -20px ${accent}55` }}
                    />
                </div>
            )}
        </>
    );
}
